import {Badge} from '@mantine/core';
import {Text} from 'src/design/atoms/Text';
import dayjs from 'dayjs';
import type {MRT_ColumnDef} from 'mantine-react-table';
import {useMemo} from 'react';
import type {AccountListResponse} from 'src/api/ProactApiClient';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';

export const useAccountColumns = () => {
  const columns = useMemo<MRT_ColumnDef<AccountListResponse>[]>(
    () => [
      {
        accessorKey: 'email',
        header: 'Email',
        size: 280,
      },
      {
        id: 'displayName',
        header: 'Display Name',
        accessorFn: row => row.displayName ?? '',
        enableSorting: false,
        Cell: ({row}) =>
          row.original.displayName ? (
            <Text size="sm">{row.original.displayName}</Text>
          ) : (
            <Text size="sm" c="dimmed">
              —
            </Text>
          ),
      },
      {
        id: 'isSuperAdmin',
        accessorKey: 'isSuperAdmin',
        header: 'Super Admin',
        filterVariant: 'checkbox',
        Cell: ({cell}) =>
          cell.getValue<boolean>() ? (
            <Badge color="green" variant="light">
              Super Admin
            </Badge>
          ) : (
            <Badge color="gray" variant="light">
              Standard
            </Badge>
          ),
      },
      {
        id: 'primaryInstance.name',
        header: 'Primary Instance',
        accessorFn: row => row.primaryInstanceName ?? '',
        Cell: ({row}) =>
          row.original.primaryInstanceName ? (
            <Text size="sm">{row.original.primaryInstanceName}</Text>
          ) : (
            <Text size="sm" c="dimmed">
              —
            </Text>
          ),
      },
      {
        id: 'membershipCount',
        header: 'Memberships',
        accessorFn: row => row.membershipCount,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: 'createdAt',
        accessorFn: row => (row.createdAt ? parseUtcDate(row.createdAt) : null),
        filterVariant: 'date',
        header: 'Created',
        Cell: ({cell}) => {
          const value = cell.getValue<Date | null>();
          return <>{value ? dayjs(value).format('MM/DD/YYYY') : ''}</>;
        },
      },
      {
        id: 'lastActiveAt',
        accessorFn: row =>
          row.lastActiveAt ? parseUtcDate(row.lastActiveAt) : null,
        filterVariant: 'date',
        header: 'Last Active',
        Cell: ({cell}) => {
          const value = cell.getValue<Date | null>();
          return <>{value ? dayjs(value).format('MM/DD/YYYY  h:mm A') : ''}</>;
        },
      },
    ],
    []
  );

  return columns;
};
